import { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { User, Mail, Phone, LogOut, ChevronRight, Shield, AlertTriangle, Settings, Moon, Sun, ClipboardList, MessageCircle, Bell, Check, Loader2, Wallet, Plus, ArrowDownLeft, ArrowUpRight, RefreshCcw, Gift, Ticket, Copy, Trash2, Leaf, Share2, Download } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { useTheme } from '@/context/ThemeContext';
import { toast } from 'sonner';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { API_URL } from '@/config/api';
import VoucherCard, { exportVouchersPDF, downloadSingleVoucherPDF, vouchersPdfBase64, defaultLoginUrl, VOUCHER_THEMES } from '@/components/app/VoucherCard';

export default function AppProfile() {
  const { user, logout, isAdmin, token, refreshUser } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [whatsappEnabled, setWhatsappEnabled] = useState(user?.whatsapp_notifications || false);
  const [smsEnabled, setSmsEnabled] = useState(user?.sms_notifications ?? true);
  const [savingSms, setSavingSms] = useState(false);
  const [lowBalanceAlerts, setLowBalanceAlerts] = useState(user?.wallet_low_balance_alerts || false);
  const [lowBalanceThreshold, setLowBalanceThreshold] = useState(user?.wallet_low_balance_threshold ?? 50);
  const [savingLowBalance, setSavingLowBalance] = useState(false);
  const [whatsappNumber, setWhatsappNumber] = useState(user?.whatsapp_number || user?.phone || '');
  const [savingWhatsapp, setSavingWhatsapp] = useState(false);
  const [showWhatsappSetup, setShowWhatsappSetup] = useState(false);
  const [walletBalance, setWalletBalance] = useState(null);
  const [walletLoading, setWalletLoading] = useState(true);
  const [showTopup, setShowTopup] = useState(false);
  const [topupAmount, setTopupAmount] = useState('');
  const [topupLoading, setTopupLoading] = useState(false);
  const [transactions, setTransactions] = useState([]);
  const [showTransactions, setShowTransactions] = useState(false);
  const [showRefund, setShowRefund] = useState(false);
  const [refundAmount, setRefundAmount] = useState('');
  const [refundLoading, setRefundLoading] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editName, setEditName] = useState(user?.name || '');
  const [editPhone, setEditPhone] = useState(user?.phone || '');
  const [editWhatsapp, setEditWhatsapp] = useState(user?.whatsapp_number || user?.phone || '');
  const [savingProfile, setSavingProfile] = useState(false);
  const [loyalty, setLoyalty] = useState(null);
  // Vouchers
  const [redeemCode, setRedeemCode] = useState('');
  const [redeemLoading, setRedeemLoading] = useState(false);
  const [showBuyVoucher, setShowBuyVoucher] = useState(false);
  const [voucherItems, setVoucherItems] = useState([{ uid: 'v1', value: '100', recipient_name: '', recipient_contact: '', message: '', theme: 'classic' }]);
  const [buyLoading, setBuyLoading] = useState(false);
  const [myVouchers, setMyVouchers] = useState([]);
  const [showMyVouchers, setShowMyVouchers] = useState(false);
  const [purchasedResult, setPurchasedResult] = useState(null);
  const [voucherEmail, setVoucherEmail] = useState('');
  const [voucherPdfBusy, setVoucherPdfBusy] = useState(false);
  const [dietPrefs, setDietPrefs] = useState(user?.dietary_preferences || []);
  const [marketingOptIn, setMarketingOptIn] = useState(user?.marketing_opt_in || false);
  const [savingPrefs, setSavingPrefs] = useState(false);
  // Referral + account linking
  const [referral, setReferral] = useState(null);
  const [linkEmail, setLinkEmail] = useState('');
  const [linkPhone, setLinkPhone] = useState('');
  const [linkPhoneOtp, setLinkPhoneOtp] = useState('');
  const [linkPhoneOtpSent, setLinkPhoneOtpSent] = useState(false);
  const [linkBusy, setLinkBusy] = useState(false);

  useEffect(() => {
    if (user) {
      setWhatsappEnabled(user.whatsapp_notifications || false);
      setSmsEnabled(user.sms_notifications ?? true);
      setWhatsappNumber(user.whatsapp_number || user.phone || '');
      setEditName(user.name || '');
      setEditPhone(user.phone || '');
      setEditWhatsapp(user.whatsapp_number || user.phone || '');
      setDietPrefs(user.dietary_preferences || []);
      setMarketingOptIn(user.marketing_opt_in || false);
    }
  }, [user]);

  // Fetch wallet balance
  const fetchWallet = useCallback(async () => {
    if (!token) return;
    try {
      const r = await fetch(`${API_URL}/api/wallet/balance`, {
        headers: { 'Authorization': `Bearer ${token}` }, credentials: 'include'
      });
      if (r.ok) {
        const d = await r.json();
        setWalletBalance(d.balance);
      }
    } catch (e) { console.warn('Wallet balance fetch failed:', e); } finally { setWalletLoading(false); }
  }, [token]);

  useEffect(() => { fetchWallet(); }, [fetchWallet]);

  // Fetch loyalty status
  useEffect(() => {
    if (!token) return;
    fetch(`${API_URL}/api/loyalty/status`, {
      headers: { 'Authorization': `Bearer ${token}` }, credentials: 'include'
    }).then(r => r.ok ? r.json() : null).then(d => { if (d) setLoyalty(d); }).catch(e => console.warn('Loyalty status fetch failed:', e));
  }, [token]);

  // Verify topup callback — Paystack redirects with ?trxref=xxx&reference=xxx
  useEffect(() => {
    const walletParam = searchParams.get('wallet');
    const ref = searchParams.get('reference') || searchParams.get('trxref');
    // Trigger verify if: wallet=topup param exists, OR reference starts with topup_ (Paystack callback)
    if (ref && (walletParam === 'topup' || ref.startsWith('topup_') || ref.includes('topup')) && token) {
      fetch(`${API_URL}/api/wallet/verify-topup/${ref}`, {
        headers: { 'Authorization': `Bearer ${token}` }, credentials: 'include'
      }).then(r => r.json()).then(d => {
        if (d.balance !== undefined) {
          setWalletBalance(d.balance);
          toast.success(d.message || 'Wallet topped up!');
        } else if (d.detail) {
          toast.error(d.detail);
        }
        window.history.replaceState({}, '', '/profile');
      }).catch(() => { /* silent */ });
    }
  }, [searchParams, token]);

  // Fetch the customer's purchased vouchers
  const fetchMyVouchers = useCallback(async () => {
    if (!token) return;
    try {
      const r = await fetch(`${API_URL}/api/vouchers/my-purchases`, {
        headers: { 'Authorization': `Bearer ${token}` }, credentials: 'include'
      });
      if (r.ok) setMyVouchers(await r.json());
    } catch (e) { console.warn('Voucher list fetch failed:', e); }
  }, [token]);

  useEffect(() => { fetchMyVouchers(); }, [fetchMyVouchers]);

  // Verify a voucher purchase after returning from Paystack (?voucher=purchase)
  useEffect(() => {
    const voucherParam = searchParams.get('voucher');
    const ref = searchParams.get('reference') || searchParams.get('trxref');
    if (ref && (voucherParam === 'purchase' || ref.startsWith('voucherbuy_')) && token) {
      fetch(`${API_URL}/api/vouchers/purchase/verify/${ref}`, {
        headers: { 'Authorization': `Bearer ${token}` }, credentials: 'include'
      }).then(r => r.json()).then(d => {
        if (d.status === 'completed') {
          toast.success('Vouchers purchased! 🎉');
          fetchMyVouchers();
          if ((d.issued || []).length) {
            setPurchasedResult(d.issued);
          } else {
            setShowMyVouchers(true);
          }
        } else {
          toast.info('Payment is still processing. Your vouchers will appear shortly.');
        }
        window.history.replaceState({}, '', '/profile');
      }).catch(() => { /* silent */ });
    }
  }, [searchParams, token, fetchMyVouchers]);

  // Auto-redeem a voucher when arriving from a scanned QR (?redeem=CODE)
  const redeemedRef = useRef(new Set());
  useEffect(() => {
    const code = (searchParams.get('redeem') || '').trim().toUpperCase();
    if (code && token && !redeemedRef.current.has(code)) {
      redeemedRef.current.add(code);
      window.history.replaceState({}, '', '/profile');
      (async () => {
        try {
          const r = await fetch(`${API_URL}/api/vouchers/redeem`, {
            method: 'POST',
            headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({ code })
          });
          const d = await r.json();
          if (!r.ok) throw new Error(d.detail || 'Could not redeem voucher');
          toast.success(d.message || 'Voucher redeemed!');
          if (d.new_balance !== undefined) setWalletBalance(d.new_balance);
        } catch (e) {
          setRedeemCode(code);
          toast.error(e.message);
        }
      })();
    }
  }, [searchParams, token]);

  // Deep-link from Home "Gift a voucher" banner → auto-open the buy modal
  useEffect(() => {
    if (searchParams.get('action') === 'buy-voucher') {
      setShowBuyVoucher(true);
      window.history.replaceState({}, '', '/profile');
    }
  }, [searchParams]);

  const handleRedeemVoucher = async () => {
    const code = redeemCode.trim().toUpperCase();
    if (!code) { toast.error('Enter a voucher code'); return; }
    setRedeemLoading(true);
    try {
      const r = await fetch(`${API_URL}/api/vouchers/redeem`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ code })
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || 'Could not redeem voucher');
      toast.success(d.message || 'Voucher redeemed!');
      if (d.new_balance !== undefined) setWalletBalance(d.new_balance);
      setRedeemCode('');
    } catch (e) { toast.error(e.message); }
    finally { setRedeemLoading(false); }
  };

  const voucherTotal = voucherItems.reduce((sum, it) => sum + (parseFloat(it.value) || 0), 0);

  const updateVoucherItem = (idx, field, value) => {
    setVoucherItems(prev => prev.map((it, i) => i === idx ? { ...it, [field]: value } : it));
  };
  const addVoucherItem = () => setVoucherItems(prev => [...prev, { uid: Math.random().toString(36).slice(2), value: '100', recipient_name: '', recipient_contact: '', message: '', theme: 'classic' }]);
  const removeVoucherItem = (idx) => setVoucherItems(prev => prev.filter((_, i) => i !== idx));

  const handleBuyVouchers = async () => {
    const items = voucherItems
      .map(it => ({ value: parseFloat(it.value) || 0, recipient_name: it.recipient_name.trim(), recipient_contact: it.recipient_contact.trim(), message: (it.message || '').trim(), theme: it.theme || 'classic' }))
      .filter(it => it.value > 0);
    if (items.length === 0) { toast.error('Add at least one voucher'); return; }
    if (items.some(it => it.value < 10)) { toast.error('Each voucher must be at least R10'); return; }
    setBuyLoading(true);
    try {
      const r = await fetch(`${API_URL}/api/vouchers/purchase`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ items, origin_url: window.location.origin })
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || 'Could not start purchase');
      window.location.href = d.authorization_url;
    } catch (e) { toast.error(e.message); setBuyLoading(false); }
  };

  const issuedToCards = (issued) => (issued || []).map(v => ({
    value: Number(v.value) || 0, code: v.code, message: v.message || '', expiry: v.valid_until || '', theme: v.theme || 'classic'
  }));

  const downloadVoucherPdf = async (issued, all = true) => {
    setVoucherPdfBusy(true);
    try {
      const cards = issuedToCards(issued);
      if (all) await exportVouchersPDF(cards, defaultLoginUrl(), 'gc3h-vouchers.pdf');
      else await downloadSingleVoucherPDF(cards[0], defaultLoginUrl());
      toast.success('Voucher downloaded');
    } catch (e) { toast.error('Could not generate PDF'); }
    finally { setVoucherPdfBusy(false); }
  };

  const emailVoucherPdf = async (issued) => {
    const email = (voucherEmail || user?.email || '').trim();
    if (!email || !email.includes('@')) { toast.error('Enter a valid email'); return; }
    setVoucherPdfBusy(true);
    try {
      const pdf_base64 = await vouchersPdfBase64(issuedToCards(issued), defaultLoginUrl());
      const r = await fetch(`${API_URL}/api/vouchers/email-pdf`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ email, pdf_base64, filename: 'gc3h-vouchers.pdf' })
      });
      let d = {};
      try { d = await r.json(); } catch { /* non-JSON (e.g. 413) */ }
      if (!r.ok) throw new Error(d.detail || (r.status === 413 ? 'PDF is too large to email — try fewer vouchers, or download instead.' : 'Failed to email PDF'));
      toast.success(d.message || 'Voucher emailed');
      setVoucherEmail('');
    } catch (e) { toast.error(e.message); }
    finally { setVoucherPdfBusy(false); }
  };

  const fetchTransactions = async () => {
    try {
      const r = await fetch(`${API_URL}/api/wallet/transactions`, {
        headers: { 'Authorization': `Bearer ${token}` }, credentials: 'include'
      });
      if (r.ok) setTransactions(await r.json());
    } catch (e) { console.warn('Transactions fetch failed:', e); }
  };

  const handleTopup = async () => {
    const amt = parseFloat(topupAmount);
    if (!amt || amt < 10) { toast.error('Minimum top-up is R10'); return; }
    setTopupLoading(true);
    try {
      const r = await fetch(`${API_URL}/api/wallet/topup`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ amount: amt, origin_url: window.location.origin })
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || 'Failed');
      window.location.href = d.authorization_url;
    } catch (e) { toast.error(e.message); setTopupLoading(false); }
  };

  const handleRefund = async () => {
    const amt = parseFloat(refundAmount);
    if (!amt || amt <= 0) { toast.error('Enter a valid amount'); return; }
    setRefundLoading(true);
    try {
      const r = await fetch(`${API_URL}/api/wallet/request-refund`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ amount: amt })
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || 'Failed');
      toast.success(d.message);
      setShowRefund(false);
      setRefundAmount('');
      fetchWallet();
    } catch (e) { toast.error(e.message); } finally { setRefundLoading(false); }
  };

  const handleSaveProfile = async () => {
    if (!editName.trim()) { toast.error('Name is required'); return; }
    setSavingProfile(true);
    try {
      const r = await fetch(`${API_URL}/api/auth/profile`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          name: editName.trim(),
          phone: editPhone.trim() || null,
          whatsapp_number: editWhatsapp.trim() || null,
          whatsapp_notifications: whatsappEnabled
        })
      });
      if (!r.ok) { const d = await r.json(); throw new Error(d.detail || 'Failed to save'); }
      toast.success('Profile updated!');
      setEditing(false);
      if (refreshUser) refreshUser();
    } catch (e) { toast.error(e.message); }
    finally { setSavingProfile(false); }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const saveLowBalance = async (enabled, threshold) => {
    setSavingLowBalance(true);
    try {
      const r = await fetch(`${API_URL}/api/auth/profile`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ wallet_low_balance_alerts: enabled, wallet_low_balance_threshold: threshold })
      });
      if (!r.ok) { const d = await r.json(); throw new Error(d.detail || 'Failed'); }
      setLowBalanceAlerts(enabled);
      toast.success(enabled ? `We'll remind you when your wallet drops below R${threshold}` : 'Low-balance reminders off');
      if (refreshUser) refreshUser();
    } catch (e) { toast.error(e.message); }
    finally { setSavingLowBalance(false); }
  };

  const toggleDietPref = async (pref) => {
    const next = dietPrefs.includes(pref) ? dietPrefs.filter(p => p !== pref) : [...dietPrefs, pref];
    setDietPrefs(next);
    try {
      const r = await fetch(`${API_URL}/api/auth/profile`, {
        method: 'PUT', headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        credentials: 'include', body: JSON.stringify({ dietary_preferences: next })
      });
      if (!r.ok) throw new Error('Failed');
      if (refreshUser) refreshUser();
    } catch { setDietPrefs(dietPrefs); toast.error('Could not save preference'); }
  };

  const saveMarketingOptIn = async () => {
    const next = !marketingOptIn;
    setSavingPrefs(true);
    try {
      const r = await fetch(`${API_URL}/api/auth/profile`, {
        method: 'PUT', headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        credentials: 'include', body: JSON.stringify({ marketing_opt_in: next })
      });
      if (!r.ok) throw new Error('Failed');
      setMarketingOptIn(next);
      toast.success(next ? "You'll get our specials & gift-card offers" : 'Unsubscribed from promotions');
      if (refreshUser) refreshUser();
    } catch { toast.error('Could not update'); }
    finally { setSavingPrefs(false); }
  };

  // Referral
  useEffect(() => {
    if (!token) return;
    fetch(`${API_URL}/api/referral/code`, { headers: { 'Authorization': `Bearer ${token}` }, credentials: 'include' })
      .then(r => r.ok ? r.json() : null).then(setReferral).catch(e => console.warn('Referral fetch failed:', e));
  }, [token]);

  const referralLink = referral ? `${window.location.origin}/auth?ref=${referral.code}` : '';
  const copyReferral = () => { navigator.clipboard?.writeText(referralLink); toast.success('Invite link copied!'); };
  const shareReferralWhatsApp = () => {
    const msg = encodeURIComponent(`Join me on GC3H Online Canteen and we both get wallet credit when you place your first order! ${referralLink}`);
    window.open(`https://wa.me/?text=${msg}`, '_blank');
  };

  // Account linking — handle email confirmation deep-link
  useEffect(() => {
    const t = searchParams.get('link_email_token');
    if (t && token) {
      fetch(`${API_URL}/api/auth/link/verify-email`, {
        method: 'POST', headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        credentials: 'include', body: JSON.stringify({ token: t })
      }).then(r => r.json()).then(d => {
        if (d.email) { toast.success('Email linked ✓'); if (refreshUser) refreshUser(); }
        else toast.error(d.detail || 'Link expired');
        window.history.replaceState({}, '', '/profile');
      }).catch(e => console.warn('Email link verify failed:', e));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, token]);

  const sendEmailLink = async () => {
    if (!linkEmail.trim()) { toast.error('Enter an email'); return; }
    setLinkBusy(true);
    try {
      const r = await fetch(`${API_URL}/api/auth/link/add-email`, {
        method: 'POST', headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        credentials: 'include', body: JSON.stringify({ email: linkEmail.trim() })
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || 'Failed');
      toast.success(d.message);
    } catch (e) { toast.error(e.message); }
    finally { setLinkBusy(false); }
  };

  const sendPhoneOtp = async () => {
    if (!linkPhone.trim()) { toast.error('Enter a phone number'); return; }
    setLinkBusy(true);
    try {
      const r = await fetch(`${API_URL}/api/auth/link/add-phone`, {
        method: 'POST', headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        credentials: 'include', body: JSON.stringify({ phone: linkPhone.trim() })
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || 'Failed');
      setLinkPhoneOtpSent(true);
      toast.success('Code sent via WhatsApp');
    } catch (e) { toast.error(e.message); }
    finally { setLinkBusy(false); }
  };

  const verifyPhoneLink = async () => {
    setLinkBusy(true);
    try {
      const r = await fetch(`${API_URL}/api/auth/link/verify-phone`, {
        method: 'POST', headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        credentials: 'include', body: JSON.stringify({ phone: linkPhone.trim(), code: linkPhoneOtp.trim() })
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || 'Failed');
      toast.success('Phone linked ✓');
      setLinkPhoneOtpSent(false); setLinkPhone(''); setLinkPhoneOtp('');
      if (refreshUser) refreshUser();
    } catch (e) { toast.error(e.message); }
    finally { setLinkBusy(false); }
  };

  const handleWhatsappToggle = async () => {
    if (!whatsappEnabled && !whatsappNumber) {
      setShowWhatsappSetup(true);
      return;
    }

    setSavingWhatsapp(true);
    try {
      const newEnabled = !whatsappEnabled;
      const response = await fetch(`${API_URL}/api/auth/profile`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          whatsapp_notifications: newEnabled,
          whatsapp_number: whatsappNumber
        })
      });

      if (response.ok) {
        setWhatsappEnabled(newEnabled);
        toast.success(newEnabled ? 'WhatsApp notifications enabled!' : 'WhatsApp notifications disabled');
        if (refreshUser) refreshUser();
      } else {
        toast.error('Failed to update settings');
      }
    } catch (error) {
      console.error('Error updating WhatsApp settings:', error);
      toast.error('Failed to update settings');
    } finally {
      setSavingWhatsapp(false);
    }
  };

  const handleSmsToggle = async () => {
    setSavingSms(true);
    try {
      const newEnabled = !smsEnabled;
      const r = await fetch(`${API_URL}/api/auth/profile`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        credentials: 'include',
        body: JSON.stringify({ sms_notifications: newEnabled })
      });
      if (!r.ok) throw new Error('Failed to update settings');
      setSmsEnabled(newEnabled);
      toast.success(newEnabled ? 'SMS notifications enabled' : 'SMS notifications disabled');
      if (refreshUser) refreshUser();
    } catch (e) { toast.error(e.message); }
    finally { setSavingSms(false); }
  };

  const handleSaveWhatsappNumber = async () => {
    if (!whatsappNumber || whatsappNumber.length < 10) {
      toast.error('Please enter a valid phone number');
      return;
    }

    setSavingWhatsapp(true);
    try {
      const response = await fetch(`${API_URL}/api/auth/profile`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          whatsapp_notifications: true,
          whatsapp_number: whatsappNumber
        })
      });

      if (response.ok) {
        setWhatsappEnabled(true);
        setShowWhatsappSetup(false);
        toast.success('WhatsApp notifications enabled!');
        if (refreshUser) refreshUser();
      } else {
        toast.error('Failed to save settings');
      }
    } catch (error) {
      console.error('Error saving WhatsApp number:', error);
      toast.error('Failed to save settings');
    } finally {
      setSavingWhatsapp(false);
    }
  };

  const menuItems = [
    { icon: ClipboardList, label: 'Order History', path: '/orders' },
    { icon: AlertTriangle, label: 'Food Safety Report', path: '/food-safety', accent: true },
    ...(isAdmin ? [{ icon: Shield, label: 'Admin Portal', path: '/admin', admin: true }] : [])
  ];

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="min-h-screen bg-background dark:bg-slate-900"
      data-testid="app-profile"
    >
      {/* Header */}
      <div className="bg-gradient-to-br from-primary to-primary-600 pt-12 pb-20 px-5">
        <h1 className="font-heading text-2xl font-semibold text-white mb-8">Profile</h1>
        
        <div className="flex items-center gap-4">
          {user?.picture ? (
            <img src={user.picture} alt={user.name} className="w-20 h-20 rounded-2xl object-cover" />
          ) : (
            <div className="w-20 h-20 bg-white/20 rounded-2xl flex items-center justify-center">
              <User className="h-10 w-10 text-white" />
            </div>
          )}
          <div>
            <h2 className="text-xl font-semibold text-white">{user?.name}</h2>
            <p className="text-white/80 text-sm">{user?.email}</p>
          </div>
        </div>
      </div>

      {/* Profile Card */}
      <div className="px-5 -mt-10">
        <div className="bg-white dark:bg-slate-800 rounded-3xl shadow-lg p-5 mb-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-secondary-900 dark:text-white">Contact Information</h3>
            {!editing ? (
              <button onClick={() => setEditing(true)} className="text-xs text-primary font-medium" data-testid="edit-profile-btn">
                Edit
              </button>
            ) : (
              <div className="flex gap-2">
                <button onClick={() => { setEditing(false); setEditName(user?.name || ''); setEditPhone(user?.phone || ''); setEditWhatsapp(user?.whatsapp_number || user?.phone || ''); }}
                  className="text-xs text-gray-500 font-medium">Cancel</button>
                <button onClick={handleSaveProfile} disabled={savingProfile}
                  className="text-xs text-primary font-semibold flex items-center gap-1" data-testid="save-profile-btn">
                  {savingProfile ? <Loader2 className="h-3 w-3 animate-spin" /> : <Check className="h-3 w-3" />} Save
                </button>
              </div>
            )}
          </div>
          <div className="space-y-4">
            {/* Name */}
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 bg-primary/10 rounded-xl flex items-center justify-center flex-shrink-0">
                <User className="h-5 w-5 text-primary" />
              </div>
              <div className="flex-1">
                <p className="text-xs text-muted-foreground">Name</p>
                {editing ? (
                  <input value={editName} onChange={e => setEditName(e.target.value)}
                    className="w-full mt-0.5 px-3 py-2 bg-gray-50 dark:bg-slate-700 rounded-lg text-sm text-secondary-900 dark:text-white border-0 focus:ring-2 focus:ring-primary/30"
                    data-testid="edit-name" />
                ) : (
                  <p className="text-secondary-900 dark:text-white">{user?.name}</p>
                )}
              </div>
            </div>
            {/* Email (read-only) */}
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 bg-primary/10 rounded-xl flex items-center justify-center flex-shrink-0">
                <Mail className="h-5 w-5 text-primary" />
              </div>
              <div className="flex-1">
                <p className="text-xs text-muted-foreground">Email</p>
                <p className="text-secondary-900 dark:text-white">{user?.email}</p>
              </div>
            </div>
            {/* Phone */}
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 bg-primary/10 rounded-xl flex items-center justify-center flex-shrink-0">
                <Phone className="h-5 w-5 text-primary" />
              </div>
              <div className="flex-1">
                <p className="text-xs text-muted-foreground">Phone</p>
                {editing ? (
                  <input value={editPhone} onChange={e => setEditPhone(e.target.value)} placeholder="+27 82 123 4567"
                    className="w-full mt-0.5 px-3 py-2 bg-gray-50 dark:bg-slate-700 rounded-lg text-sm text-secondary-900 dark:text-white border-0 focus:ring-2 focus:ring-primary/30"
                    data-testid="edit-phone" />
                ) : (
                  <p className="text-secondary-900 dark:text-white">{user?.phone || 'Not provided'}</p>
                )}
              </div>
            </div>
            {/* WhatsApp Number */}
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 bg-green-100 dark:bg-green-900/30 rounded-xl flex items-center justify-center flex-shrink-0">
                <MessageCircle className="h-5 w-5 text-green-600" />
              </div>
              <div className="flex-1">
                <p className="text-xs text-muted-foreground">WhatsApp Number</p>
                {editing ? (
                  <input value={editWhatsapp} onChange={e => setEditWhatsapp(e.target.value)} placeholder="+27 82 123 4567"
                    className="w-full mt-0.5 px-3 py-2 bg-gray-50 dark:bg-slate-700 rounded-lg text-sm text-secondary-900 dark:text-white border-0 focus:ring-2 focus:ring-primary/30"
                    data-testid="edit-whatsapp" />
                ) : (
                  <p className="text-secondary-900 dark:text-white">{user?.whatsapp_number || user?.phone || 'Not provided'}</p>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Wallet Card */}
        <div className="bg-white dark:bg-slate-800 rounded-3xl shadow-lg overflow-hidden mb-4">
          <div className="bg-gradient-to-r from-primary/10 to-amber-50 dark:from-primary/20 dark:to-amber-900/20 p-5">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Wallet className="h-5 w-5 text-primary" />
                <span className="font-semibold text-secondary-900 dark:text-white">My Wallet</span>
              </div>
              <button
                onClick={() => { setShowTransactions(!showTransactions); if (!showTransactions) fetchTransactions(); }}
                className="text-xs text-primary font-medium"
                data-testid="view-transactions"
              >
                {showTransactions ? 'Hide' : 'History'}
              </button>
            </div>
            <div className="flex items-end justify-between">
              <div>
                <p className="text-xs text-muted-foreground mb-0.5">Available Balance</p>
                <p className="text-3xl font-black text-secondary-900 dark:text-white">
                  {walletLoading ? '...' : `R${(walletBalance || 0).toFixed(2)}`}
                </p>
              </div>
              <div className="flex gap-2">
                {walletBalance > 0 && (
                  <button
                    onClick={() => setShowRefund(true)}
                    className="h-10 px-3 bg-white dark:bg-slate-700 rounded-xl text-sm font-medium text-gray-600 dark:text-gray-300 border border-gray-200 dark:border-slate-600 flex items-center gap-1.5"
                    data-testid="refund-btn"
                  >
                    <RefreshCcw className="h-3.5 w-3.5" />
                    Refund
                  </button>
                )}
                <button
                  onClick={() => setShowTopup(true)}
                  className="h-10 px-4 bg-primary rounded-xl text-sm font-semibold text-white flex items-center gap-1.5 shadow-sm"
                  data-testid="topup-btn"
                >
                  <Plus className="h-4 w-4" />
                  Top Up
                </button>
              </div>
            </div>
          </div>
          <div className="px-5 py-3 border-t border-border/50 dark:border-slate-700">
            <p className="text-xs text-muted-foreground">
              Use your wallet balance to pay for orders instantly at checkout — no card needed.
            </p>
          </div>

          {/* Low-balance reminder */}
          <div className="px-5 py-3 border-t border-border/50 dark:border-slate-700" data-testid="low-balance-setting">
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-2">
                <Bell className={`h-4 w-4 ${lowBalanceAlerts ? 'text-primary' : 'text-slate-400'}`} />
                <div>
                  <p className="text-sm font-medium text-secondary-900 dark:text-white">Low-balance reminder</p>
                  <p className="text-xs text-muted-foreground">Get nudged to top up when your wallet runs low</p>
                </div>
              </div>
              <button
                onClick={() => saveLowBalance(!lowBalanceAlerts, Number(lowBalanceThreshold) || 50)}
                disabled={savingLowBalance}
                className={`relative w-11 h-6 rounded-full transition-colors ${lowBalanceAlerts ? 'bg-primary' : 'bg-slate-200 dark:bg-slate-600'}`}
                data-testid="low-balance-toggle"
              >
                <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${lowBalanceAlerts ? 'translate-x-5' : ''}`} />
              </button>
            </div>
            {lowBalanceAlerts && (
              <div className="flex items-center gap-2 mt-3">
                <span className="text-xs text-muted-foreground">Remind me below</span>
                <div className="flex items-center gap-1">
                  <span className="text-sm font-medium">R</span>
                  <input
                    type="number" min="0" value={lowBalanceThreshold}
                    onChange={e => setLowBalanceThreshold(e.target.value)}
                    onBlur={() => saveLowBalance(true, Number(lowBalanceThreshold) || 50)}
                    className="w-20 h-8 px-2 rounded-lg border border-border bg-muted/30 text-sm"
                    data-testid="low-balance-threshold"
                  />
                </div>
              </div>
            )}
          </div>

          {/* Transaction History */}
          <AnimatePresence>
            {showTransactions && (
              <motion.div initial={{ height: 0 }} animate={{ height: 'auto' }} exit={{ height: 0 }} className="overflow-hidden">
                <div className="border-t border-border/50 dark:border-slate-700 px-5 py-3 max-h-60 overflow-y-auto">
                  {transactions.length === 0 ? (
                    <p className="text-sm text-muted-foreground text-center py-4">No transactions yet</p>
                  ) : transactions.map((txn, idx) => (
                    <div key={txn.transaction_id || idx} className="flex items-center justify-between py-2.5 border-b border-border/30 dark:border-slate-700/50 last:border-0">
                      <div className="flex items-center gap-3">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                          txn.type === 'topup' ? 'bg-green-100 dark:bg-green-900/30' : txn.type === 'refund_request' ? 'bg-amber-100 dark:bg-amber-900/30' : 'bg-red-100 dark:bg-red-900/30'
                        }`}>
                          {txn.type === 'topup' ? <ArrowDownLeft className="h-4 w-4 text-green-600" /> :
                           txn.type === 'refund_request' ? <RefreshCcw className="h-3.5 w-3.5 text-amber-600" /> :
                           <ArrowUpRight className="h-4 w-4 text-red-600" />}
                        </div>
                        <div>
                          <p className="text-sm font-medium text-secondary-900 dark:text-white">
                            {txn.type === 'topup' ? 'Top Up' : txn.type === 'payment' ? 'Order Payment' : 'Refund Request'}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {new Date(txn.created_at).toLocaleDateString('en-ZA', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
                          </p>
                        </div>
                      </div>
                      <span className={`font-semibold text-sm ${txn.amount > 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {txn.amount > 0 ? '+' : ''}R{Math.abs(txn.amount).toFixed(2)}
                      </span>
                    </div>
                  ))}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Vouchers & Gift Cards Card */}
        <div className="bg-white dark:bg-slate-800 rounded-3xl shadow-lg overflow-hidden mb-4" data-testid="vouchers-card">
          <div className="bg-gradient-to-r from-rose-50 to-amber-50 dark:from-rose-900/20 dark:to-amber-900/20 p-5">
            <div className="flex items-center gap-2 mb-4">
              <Gift className="h-5 w-5 text-primary" />
              <span className="font-semibold text-secondary-900 dark:text-white">Vouchers &amp; Gift Cards</span>
            </div>

            {/* Redeem a voucher */}
            <div>
              <p className="text-xs font-medium text-muted-foreground mb-1.5 flex items-center gap-1">
                <Ticket className="h-3.5 w-3.5" /> Have a voucher code?
              </p>
              <div className="flex gap-2">
                <input
                  value={redeemCode}
                  onChange={e => setRedeemCode(e.target.value.toUpperCase())}
                  placeholder="Enter code"
                  className="flex-1 h-11 px-3 bg-white dark:bg-slate-700 rounded-xl border border-border dark:border-slate-600 text-sm font-mono uppercase tracking-wider text-secondary-900 dark:text-white focus:ring-2 focus:ring-primary/30"
                  data-testid="voucher-redeem-input"
                />
                <button
                  onClick={handleRedeemVoucher}
                  disabled={redeemLoading || !redeemCode.trim()}
                  className="h-11 px-4 bg-primary text-white rounded-xl text-sm font-semibold flex items-center justify-center gap-1.5 disabled:opacity-50"
                  data-testid="voucher-redeem-btn"
                >
                  {redeemLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Redeem'}
                </button>
              </div>
              <p className="text-[11px] text-muted-foreground mt-1.5">Rand-value vouchers are added straight to your wallet.</p>
            </div>
          </div>

          {/* Buy / gift vouchers */}
          <div className="px-5 py-3 border-t border-border/50 dark:border-slate-700 flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-secondary-900 dark:text-white">Buy or gift vouchers</p>
              <p className="text-xs text-muted-foreground">Send a gift card to a colleague or friend</p>
            </div>
            <button
              onClick={() => setShowBuyVoucher(true)}
              className="h-10 px-4 bg-secondary-900 dark:bg-slate-700 text-white rounded-xl text-sm font-semibold flex items-center gap-1.5"
              data-testid="buy-voucher-btn"
            >
              <Gift className="h-4 w-4" /> Buy
            </button>
          </div>

          {/* My vouchers */}
          <div className="px-5 py-3 border-t border-border/50 dark:border-slate-700">
            <button
              onClick={() => { setShowMyVouchers(!showMyVouchers); if (!showMyVouchers) fetchMyVouchers(); }}
              className="w-full flex items-center justify-between"
              data-testid="my-vouchers-toggle"
            >
              <span className="text-sm font-medium text-secondary-900 dark:text-white">My vouchers</span>
              <span className="text-xs text-primary font-medium">{showMyVouchers ? 'Hide' : 'View'}</span>
            </button>
            <AnimatePresence>
              {showMyVouchers && (
                <motion.div initial={{ height: 0 }} animate={{ height: 'auto' }} exit={{ height: 0 }} className="overflow-hidden">
                  <div className="mt-3 space-y-3 max-h-60 overflow-y-auto" data-testid="my-vouchers-list">
                    {myVouchers.length === 0 ? (
                      <p className="text-sm text-muted-foreground text-center py-3">No purchased vouchers yet</p>
                    ) : myVouchers.map((p) => (
                      <div key={p.purchase_id} className="bg-gray-50 dark:bg-slate-700/50 rounded-xl p-3">
                        <div className="flex items-center justify-between mb-1.5">
                          <p className="text-[11px] text-muted-foreground">
                            {new Date(p.created_at).toLocaleDateString('en-ZA', { day: 'numeric', month: 'short', year: 'numeric' })} · R{(p.total || 0).toFixed(2)}
                          </p>
                          {(p.issued || []).length > 0 && (
                            <button onClick={() => downloadVoucherPdf(p.issued, true)} disabled={voucherPdfBusy}
                              className="text-[11px] text-primary font-medium flex items-center gap-1" data-testid={`download-purchase-${p.purchase_id}`}>
                              <Download className="h-3 w-3" /> PDF
                            </button>
                          )}
                        </div>
                        {(p.issued || []).map((v) => (
                          <div key={v.code} className="flex items-center justify-between py-1.5">
                            <div>
                              <button
                                onClick={() => { navigator.clipboard?.writeText(v.code); toast.success('Code copied'); }}
                                className="font-mono font-bold text-sm text-primary flex items-center gap-1"
                              >
                                {v.code} <Copy className="h-3 w-3" />
                              </button>
                              {(v.recipient_name || v.recipient_contact) && (
                                <p className="text-[11px] text-muted-foreground">For: {v.recipient_name || v.recipient_contact}</p>
                              )}
                            </div>
                            <div className="flex items-center gap-2">
                              <button onClick={() => downloadVoucherPdf([v], false)} disabled={voucherPdfBusy} className="text-muted-foreground hover:text-primary" title="Download voucher" data-testid={`download-voucher-${v.code}`}>
                                <Download className="h-4 w-4" />
                              </button>
                              <span className="text-sm font-semibold text-secondary-900 dark:text-white">R{(v.value || 0).toFixed(2)}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    ))}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>

        {/* Invite & earn (referrals) */}
        <div className="bg-white dark:bg-slate-800 rounded-3xl shadow-lg overflow-hidden mb-4" data-testid="referral-card">
          <div className="bg-gradient-to-r from-violet-600 to-primary p-5 text-white">
            <div className="flex items-center gap-2 mb-1">
              <Share2 className="h-5 w-5" />
              <span className="font-semibold">Invite &amp; earn</span>
            </div>
            <p className="text-white/85 text-xs">Earn <strong>R{referral?.reward_amount ?? 15}</strong> when a friend you invite spends <strong>R{referral?.min_spend ?? 70}+</strong> on their first order.</p>
            <button onClick={() => navigate('/rewards-guide')} className="mt-2 text-[11px] font-semibold underline text-white/90" data-testid="rewards-guide-link">
              How rewards work — read the full guide
            </button>
          </div>
          {referral && (
            <div className="p-5">
              <p className="text-xs text-muted-foreground mb-1.5">Your invite code</p>
              <div className="flex gap-2 mb-3">
                <div className="flex-1 h-12 bg-secondary-50 dark:bg-slate-700 rounded-xl flex items-center justify-center font-mono font-bold text-lg tracking-widest text-primary" data-testid="referral-code">
                  {referral.code}
                </div>
                <button onClick={copyReferral} className="h-12 px-4 bg-secondary-900 dark:bg-slate-600 text-white rounded-xl flex items-center gap-1.5 text-sm font-medium" data-testid="referral-copy">
                  <Copy className="h-4 w-4" /> Copy
                </button>
              </div>
              <button onClick={shareReferralWhatsApp} className="w-full h-11 bg-green-500 text-white rounded-xl font-semibold flex items-center justify-center gap-2 text-sm" data-testid="referral-share">
                <MessageCircle className="h-4 w-4" /> Share via WhatsApp
              </button>
              <div className="grid grid-cols-3 gap-2 mt-4 text-center">
                <div className="bg-secondary-50 dark:bg-slate-700 rounded-xl py-2.5">
                  <p className="text-lg font-bold text-secondary-900 dark:text-white">{referral.total_referrals || 0}</p>
                  <p className="text-[10px] text-muted-foreground">Invited</p>
                </div>
                <div className="bg-secondary-50 dark:bg-slate-700 rounded-xl py-2.5">
                  <p className="text-lg font-bold text-secondary-900 dark:text-white">{referral.rewarded_referrals || 0}</p>
                  <p className="text-[10px] text-muted-foreground">Ordered</p>
                </div>
                <div className="bg-secondary-50 dark:bg-slate-700 rounded-xl py-2.5">
                  <p className="text-lg font-bold text-primary" data-testid="referral-earned">R{referral.total_earned || 0}</p>
                  <p className="text-[10px] text-muted-foreground">Earned</p>
                </div>
              </div>

              {/* Free-lunch milestone progress */}
              {(referral.milestone_threshold ?? 0) > 0 && (
                <div className="mt-4 rounded-xl p-3.5 bg-gradient-to-r from-amber-50 to-orange-50 dark:from-amber-900/20 dark:to-orange-900/20 border border-amber-200 dark:border-amber-800/50" data-testid="referral-milestone">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-xs font-semibold text-amber-900 dark:text-amber-200 flex items-center gap-1.5">
                      <Gift className="h-4 w-4" /> Refer {referral.milestone_threshold}, get a free lunch
                    </p>
                    {(referral.free_lunches_earned ?? 0) > 0 && (
                      <span className="text-[10px] font-bold bg-amber-500 text-white px-2 py-0.5 rounded-full" data-testid="free-lunches-count">
                        {referral.free_lunches_earned} earned 🎉
                      </span>
                    )}
                  </div>
                  <div className="w-full h-2.5 bg-amber-100 dark:bg-amber-950/50 rounded-full overflow-hidden">
                    <div className="h-full bg-gradient-to-r from-amber-400 to-orange-500 rounded-full transition-all"
                      style={{ width: `${Math.round(((referral.progress_to_next || 0) / referral.milestone_threshold) * 100)}%` }}
                      data-testid="milestone-progress-bar" />
                  </div>
                  <p className="text-[11px] text-amber-800 dark:text-amber-300 mt-1.5">
                    {(referral.remaining_to_next ?? referral.milestone_threshold) === 0
                      ? `You're on track — your next free lunch unlocks soon!`
                      : <>{referral.remaining_to_next} more friend{referral.remaining_to_next === 1 ? '' : 's'} who order{referral.remaining_to_next === 1 ? 's' : ''} R{referral.min_spend ?? 70}+ and you get <strong>R{referral.milestone_reward ?? 70} free</strong> in your wallet.</>}
                  </p>
                  {referral.rank && (
                    <p className="text-[10px] text-amber-700/80 dark:text-amber-400/80 mt-1.5" data-testid="referral-rank">
                      🏆 You're ranked <strong>#{referral.rank}</strong> of {referral.total_referrers} referrer{referral.total_referrers === 1 ? '' : 's'}.
                    </p>
                  )}
                </div>
              )}
              <div className="mt-4 bg-secondary-50 dark:bg-slate-700/50 rounded-xl p-3" data-testid="referral-terms">
                <p className="text-[11px] font-semibold text-secondary-900 dark:text-white mb-1">How it works</p>
                <ul className="text-[11px] text-muted-foreground space-y-1 list-disc pl-4">
                  <li>Share your code with a friend who's new to GC3H.</li>
                  <li>You earn <strong>R{referral.reward_amount ?? 15}</strong> when they spend <strong>R{referral.min_spend ?? 70} or more</strong> on their first order.{(referral.referee_reward ?? 0) > 0 ? <> Your friend gets <strong>R{referral.referee_reward}</strong> too.</> : null}</li>
                  <li>The reward is paid <strong>once per friend</strong> and added <strong>automatically to your wallet</strong> — no claiming needed.</li>
                </ul>
              </div>
            </div>
          )}
        </div>

        {/* Account linking */}
        {(!user?.email || !user?.phone) && (
          <div className="bg-white dark:bg-slate-800 rounded-3xl shadow-lg overflow-hidden mb-4" data-testid="account-linking-card">
            <div className="px-5 py-4 border-b border-border/50 dark:border-slate-700">
              <p className="font-semibold text-secondary-900 dark:text-white flex items-center gap-2"><Shield className="h-4 w-4 text-primary" /> Secure your account</p>
              <p className="text-xs text-muted-foreground mt-0.5">Add a backup way to sign in &amp; get receipts</p>
            </div>
            {!user?.email && (
              <div className="px-5 py-4 border-b border-border/50 dark:border-slate-700">
                <p className="text-sm font-medium text-secondary-900 dark:text-white mb-2 flex items-center gap-1.5"><Mail className="h-4 w-4" /> Add an email</p>
                <div className="flex gap-2">
                  <input type="email" value={linkEmail} onChange={e => setLinkEmail(e.target.value)} placeholder="you@email.com"
                    className="flex-1 h-11 px-3 bg-secondary-50 dark:bg-slate-700 rounded-xl text-sm text-secondary-900 dark:text-white border-0" data-testid="link-email-input" />
                  <button onClick={sendEmailLink} disabled={linkBusy} className="h-11 px-4 bg-primary text-white rounded-xl text-sm font-semibold disabled:opacity-50" data-testid="link-email-btn">Send link</button>
                </div>
              </div>
            )}
            {!user?.phone && (
              <div className="px-5 py-4">
                <p className="text-sm font-medium text-secondary-900 dark:text-white mb-2 flex items-center gap-1.5"><Phone className="h-4 w-4" /> Add a phone number</p>
                {!linkPhoneOtpSent ? (
                  <div className="flex gap-2">
                    <input type="tel" value={linkPhone} onChange={e => setLinkPhone(e.target.value)} placeholder="082 123 4567"
                      className="flex-1 h-11 px-3 bg-secondary-50 dark:bg-slate-700 rounded-xl text-sm text-secondary-900 dark:text-white border-0" data-testid="link-phone-input" />
                    <button onClick={sendPhoneOtp} disabled={linkBusy} className="h-11 px-4 bg-primary text-white rounded-xl text-sm font-semibold disabled:opacity-50" data-testid="link-phone-send-btn">Send code</button>
                  </div>
                ) : (
                  <div className="flex gap-2">
                    <input type="text" inputMode="numeric" maxLength={6} value={linkPhoneOtp} onChange={e => setLinkPhoneOtp(e.target.value.replace(/\D/g, ''))} placeholder="WhatsApp code"
                      className="flex-1 h-11 px-3 bg-secondary-50 dark:bg-slate-700 rounded-xl text-sm font-mono tracking-widest text-secondary-900 dark:text-white border-0" data-testid="link-phone-otp-input" />
                    <button onClick={verifyPhoneLink} disabled={linkBusy} className="h-11 px-4 bg-green-500 text-white rounded-xl text-sm font-semibold disabled:opacity-50" data-testid="link-phone-verify-btn">Verify</button>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Preferences: dietary profile + marketing consent */}
        <div className="bg-white dark:bg-slate-800 rounded-3xl shadow-lg overflow-hidden mb-4" data-testid="preferences-card">
          <div className="px-5 py-4 border-b border-border/50 dark:border-slate-700">
            <p className="font-semibold text-secondary-900 dark:text-white flex items-center gap-2"><Leaf className="h-4 w-4 text-primary" /> Dietary profile</p>
            <p className="text-xs text-muted-foreground mt-0.5">We&apos;ll auto-filter the menu to match your choices</p>
            <div className="flex flex-wrap gap-2 mt-3">
              {[{ k: 'vegetarian', l: 'Vegetarian' }, { k: 'halaal', l: 'Halaal' }, { k: 'gluten_free', l: 'Gluten-free' }].map(opt => (
                <button key={opt.k} onClick={() => toggleDietPref(opt.k)}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${dietPrefs.includes(opt.k) ? 'bg-primary text-white' : 'bg-muted text-secondary-900 dark:text-white'}`}
                  data-testid={`diet-pref-${opt.k}`}>{opt.l}</button>
              ))}
            </div>
          </div>
          <div className="px-5 py-4 flex items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <Bell className={`h-4 w-4 ${marketingOptIn ? 'text-primary' : 'text-slate-400'}`} />
              <div>
                <p className="text-sm font-medium text-secondary-900 dark:text-white">Promotions &amp; offers</p>
                <p className="text-xs text-muted-foreground">Payday &amp; holiday gift-card deals via email/WhatsApp</p>
              </div>
            </div>
            <button onClick={saveMarketingOptIn} disabled={savingPrefs}
              className={`relative w-11 h-6 rounded-full transition-colors flex-shrink-0 ${marketingOptIn ? 'bg-primary' : 'bg-slate-200 dark:bg-slate-600'}`}
              data-testid="marketing-optin-toggle">
              <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${marketingOptIn ? 'translate-x-5' : ''}`} />
            </button>
          </div>
        </div>

        {/* Buy Voucher Modal */}
        <AnimatePresence>
          {showBuyVoucher && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/60 z-50 flex items-end sm:items-center justify-center" onClick={() => setShowBuyVoucher(false)}>
              <motion.div initial={{ y: '100%' }} animate={{ y: 0 }} exit={{ y: '100%' }}
                transition={{ type: 'spring', damping: 30, stiffness: 300 }}
                className="bg-white dark:bg-slate-800 w-full sm:max-w-md sm:rounded-2xl rounded-t-3xl p-6 max-h-[90dvh] overflow-y-auto"
                onClick={e => e.stopPropagation()} data-testid="buy-voucher-modal">
                <div className="flex items-center gap-3 mb-5">
                  <div className="w-12 h-12 bg-primary/10 rounded-2xl flex items-center justify-center">
                    <Gift className="h-6 w-6 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-bold text-lg text-secondary-900 dark:text-white">Buy Vouchers</h3>
                    <p className="text-xs text-muted-foreground">Each code can be redeemed once into a wallet</p>
                  </div>
                </div>

                <div className="space-y-4">
                  {voucherItems.map((it, idx) => (
                    <div key={it.uid || idx} className="bg-gray-50 dark:bg-slate-700/50 rounded-2xl p-4" data-testid={`voucher-item-${idx}`}>
                      <div className="flex items-center justify-between mb-3">
                        <span className="text-xs font-semibold text-secondary-900 dark:text-white">Voucher {idx + 1}</span>
                        {voucherItems.length > 1 && (
                          <button onClick={() => removeVoucherItem(idx)} className="text-red-500" data-testid={`remove-voucher-${idx}`}>
                            <Trash2 className="h-4 w-4" />
                          </button>
                        )}
                      </div>
                      <div className="grid grid-cols-4 gap-2 mb-3">
                        {[50, 100, 200, 500].map(amt => (
                          <button key={amt} type="button" onClick={() => updateVoucherItem(idx, 'value', String(amt))}
                            className={`py-2 rounded-lg text-xs font-semibold transition-colors ${
                              it.value === String(amt) ? 'bg-primary text-white' : 'bg-white dark:bg-slate-600 text-secondary-900 dark:text-white'
                            }`}>R{amt}</button>
                        ))}
                      </div>
                      <div className="relative mb-3">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 font-semibold text-sm">R</span>
                        <input type="number" value={it.value} min="10" max="20000"
                          onChange={e => updateVoucherItem(idx, 'value', e.target.value)}
                          placeholder="Amount"
                          className="w-full h-11 pl-8 pr-3 bg-white dark:bg-slate-600 rounded-xl border-0 text-sm font-bold text-secondary-900 dark:text-white"
                          data-testid={`voucher-value-${idx}`} />
                      </div>
                      <div className="mb-3">
                        <p className="text-[11px] text-muted-foreground mb-1.5">Occasion / design</p>
                        <div className="flex flex-wrap gap-1.5">
                          {Object.entries(VOUCHER_THEMES).map(([key, t]) => (
                            <button key={key} type="button" onClick={() => updateVoucherItem(idx, 'theme', key)}
                              style={(it.theme || 'classic') === key ? { backgroundColor: t.main, color: '#fff' } : {}}
                              className={`px-2.5 py-1.5 rounded-lg text-[11px] font-semibold transition-colors ${
                                (it.theme || 'classic') === key ? '' : 'bg-white dark:bg-slate-600 text-secondary-900 dark:text-white'
                              }`}
                              data-testid={`voucher-theme-${key}-${idx}`}>{t.label}</button>
                          ))}
                        </div>
                      </div>
                      <input value={it.recipient_name}
                        onChange={e => updateVoucherItem(idx, 'recipient_name', e.target.value)}
                        placeholder="Recipient name (optional)"
                        className="w-full h-10 px-3 mb-2 bg-white dark:bg-slate-600 rounded-xl border-0 text-sm text-secondary-900 dark:text-white"
                        data-testid={`voucher-recipient-name-${idx}`} />
                      <input value={it.recipient_contact}
                        onChange={e => updateVoucherItem(idx, 'recipient_contact', e.target.value)}
                        placeholder="Their email or WhatsApp (optional)"
                        className="w-full h-10 px-3 bg-white dark:bg-slate-600 rounded-xl border-0 text-sm text-secondary-900 dark:text-white"
                        data-testid={`voucher-recipient-contact-${idx}`} />
                      <input value={it.message} maxLength={120}
                        onChange={e => updateVoucherItem(idx, 'message', e.target.value)}
                        placeholder="Personal message (optional) e.g. Happy Birthday!"
                        className="w-full h-10 px-3 mt-2 bg-white dark:bg-slate-600 rounded-xl border-0 text-sm text-secondary-900 dark:text-white"
                        data-testid={`voucher-message-${idx}`} />
                    </div>
                  ))}

                  <button onClick={addVoucherItem}
                    className="w-full h-11 border border-dashed border-primary/40 text-primary rounded-xl text-sm font-medium flex items-center justify-center gap-1.5"
                    data-testid="add-voucher-item-btn">
                    <Plus className="h-4 w-4" /> Add another voucher
                  </button>
                </div>

                <div className="flex items-center justify-between mt-5 mb-4">
                  <span className="text-sm text-muted-foreground">Total</span>
                  <span className="text-2xl font-black text-secondary-900 dark:text-white">R{voucherTotal.toFixed(2)}</span>
                </div>

                <div className="flex gap-3">
                  <button onClick={() => setShowBuyVoucher(false)}
                    className="flex-1 h-12 bg-gray-100 dark:bg-slate-700 text-gray-700 dark:text-gray-300 rounded-xl font-medium">Cancel</button>
                  <button onClick={handleBuyVouchers} disabled={buyLoading || voucherTotal < 10}
                    className="flex-[2] h-12 bg-primary text-white rounded-xl font-semibold flex items-center justify-center gap-2 disabled:opacity-50"
                    data-testid="confirm-buy-voucher">
                    {buyLoading ? <Loader2 className="h-5 w-5 animate-spin" /> : <><Gift className="h-4 w-4" />Pay R{voucherTotal.toFixed(2)}</>}
                  </button>
                </div>
                <p className="text-[11px] text-muted-foreground text-center mt-3">Secure payment via Paystack · Vouchers are single-use, redeemable into any GC3H wallet, and valid for 12 months from purchase.</p>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Purchased vouchers result modal */}
        <AnimatePresence>
          {purchasedResult && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/60 z-50 flex items-end sm:items-center justify-center" onClick={() => setPurchasedResult(null)}>
              <motion.div initial={{ y: '100%' }} animate={{ y: 0 }} exit={{ y: '100%' }}
                transition={{ type: 'spring', damping: 30, stiffness: 300 }}
                className="bg-white dark:bg-slate-800 w-full sm:max-w-md sm:rounded-2xl rounded-t-3xl p-6 max-h-[90dvh] overflow-y-auto"
                onClick={e => e.stopPropagation()} data-testid="purchased-vouchers-modal">
                <div className="text-center mb-4">
                  <div className="w-12 h-12 bg-green-100 rounded-2xl flex items-center justify-center mx-auto mb-2">
                    <Check className="h-6 w-6 text-green-600" />
                  </div>
                  <h3 className="font-bold text-lg text-secondary-900 dark:text-white">Your voucher{purchasedResult.length > 1 ? 's are' : ' is'} ready!</h3>
                  <p className="text-xs text-muted-foreground">Download the branded PDF or email it. The QR redeems into a GC3H wallet.</p>
                </div>
                <div className="rounded-xl overflow-hidden border bg-slate-50 flex justify-center mb-3" style={{ height: 205 }}>
                  <div style={{ transform: 'scale(0.46)', transformOrigin: 'top center' }}>
                    <VoucherCard {...issuedToCards(purchasedResult)[0]} />
                  </div>
                </div>
                {purchasedResult.length > 1 && (
                  <p className="text-[11px] text-center text-muted-foreground mb-2">Showing 1 of {purchasedResult.length}. The PDF includes all of them.</p>
                )}
                <button onClick={() => downloadVoucherPdf(purchasedResult, true)} disabled={voucherPdfBusy}
                  className="w-full h-11 bg-primary text-white rounded-xl font-semibold flex items-center justify-center gap-2 disabled:opacity-50" data-testid="download-purchased-vouchers">
                  <Download className="h-4 w-4" /> {voucherPdfBusy ? 'Working...' : `Download ${purchasedResult.length > 1 ? 'all ' : ''}PDF`}
                </button>
                <div className="flex gap-2 mt-3">
                  <input type="email" value={voucherEmail} onChange={e => setVoucherEmail(e.target.value)}
                    placeholder={user?.email || 'email@address.com'}
                    className="flex-1 h-11 px-3 bg-gray-50 dark:bg-slate-700 rounded-xl border-0 text-sm text-secondary-900 dark:text-white" data-testid="purchased-voucher-email" />
                  <button onClick={() => emailVoucherPdf(purchasedResult)} disabled={voucherPdfBusy}
                    className="px-4 h-11 border border-primary text-primary rounded-xl text-sm font-semibold flex items-center gap-1 disabled:opacity-50" data-testid="email-purchased-vouchers">
                    <Mail className="h-4 w-4" /> Email
                  </button>
                </div>
                <button onClick={() => { setPurchasedResult(null); setShowMyVouchers(true); }}
                  className="w-full h-11 mt-3 bg-gray-100 dark:bg-slate-700 text-gray-700 dark:text-gray-300 rounded-xl font-medium">Done</button>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Top Up Modal */}
        <AnimatePresence>
          {showTopup && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/60 z-50 flex items-end sm:items-center justify-center" onClick={() => setShowTopup(false)}>
              <motion.div initial={{ y: '100%' }} animate={{ y: 0 }} exit={{ y: '100%' }}
                transition={{ type: 'spring', damping: 30, stiffness: 300 }}
                className="bg-white dark:bg-slate-800 w-full sm:max-w-sm sm:rounded-2xl rounded-t-3xl p-6"
                onClick={e => e.stopPropagation()}>
                <div className="flex items-center gap-3 mb-5">
                  <div className="w-12 h-12 bg-primary/10 rounded-2xl flex items-center justify-center">
                    <Wallet className="h-6 w-6 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-bold text-lg text-secondary-900 dark:text-white">Top Up Wallet</h3>
                    <p className="text-xs text-muted-foreground">Add funds via card payment</p>
                  </div>
                </div>

                {/* Quick amounts */}
                <div className="grid grid-cols-4 gap-2 mb-4">
                  {[50, 100, 250, 500].map(amt => (
                    <button key={amt} onClick={() => setTopupAmount(String(amt))}
                      className={`py-2.5 rounded-xl text-sm font-semibold transition-colors ${
                        topupAmount === String(amt) ? 'bg-primary text-white' : 'bg-gray-100 dark:bg-slate-700 text-secondary-900 dark:text-white'
                      }`}>R{amt}</button>
                  ))}
                </div>

                <div className="mb-5">
                  <label className="text-sm font-medium text-secondary-900 dark:text-white mb-1.5 block">Custom Amount</label>
                  <div className="relative">
                    <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 font-semibold">R</span>
                    <input type="number" value={topupAmount} onChange={e => setTopupAmount(e.target.value)}
                      placeholder="0.00" min="10" max="10000"
                      className="w-full h-14 pl-10 pr-4 bg-gray-50 dark:bg-slate-700 rounded-2xl border-0 text-2xl font-bold text-secondary-900 dark:text-white focus:ring-2 focus:ring-primary/30"
                      data-testid="topup-amount" />
                  </div>
                  <p className="text-xs text-muted-foreground mt-1.5">Min R10 · Max R10,000</p>
                </div>

                <div className="flex gap-3">
                  <button onClick={() => setShowTopup(false)}
                    className="flex-1 h-12 bg-gray-100 dark:bg-slate-700 text-gray-700 dark:text-gray-300 rounded-xl font-medium">Cancel</button>
                  <button onClick={handleTopup} disabled={topupLoading || !topupAmount || parseFloat(topupAmount) < 10}
                    className="flex-[2] h-12 bg-primary text-white rounded-xl font-semibold flex items-center justify-center gap-2 disabled:opacity-50"
                    data-testid="confirm-topup">
                    {topupLoading ? <Loader2 className="h-5 w-5 animate-spin" /> : <><Plus className="h-4 w-4" />Add R{topupAmount || '0'}</>}
                  </button>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Refund Modal */}
        <AnimatePresence>
          {showRefund && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/60 z-50 flex items-end sm:items-center justify-center" onClick={() => setShowRefund(false)}>
              <motion.div initial={{ y: '100%' }} animate={{ y: 0 }} exit={{ y: '100%' }}
                transition={{ type: 'spring', damping: 30, stiffness: 300 }}
                className="bg-white dark:bg-slate-800 w-full sm:max-w-sm sm:rounded-2xl rounded-t-3xl p-6"
                onClick={e => e.stopPropagation()}>
                <h3 className="font-bold text-lg text-secondary-900 dark:text-white mb-1">Request Refund</h3>
                <p className="text-xs text-muted-foreground mb-4">Balance: R{(walletBalance || 0).toFixed(2)} · Refund takes 5-10 business days</p>
                <div className="relative mb-4">
                  <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 font-semibold">R</span>
                  <input type="number" value={refundAmount} onChange={e => setRefundAmount(e.target.value)}
                    placeholder="0.00" max={walletBalance || 0}
                    className="w-full h-14 pl-10 pr-4 bg-gray-50 dark:bg-slate-700 rounded-2xl border-0 text-2xl font-bold text-secondary-900 dark:text-white"
                    data-testid="refund-amount" />
                </div>
                <div className="flex gap-3">
                  <button onClick={() => setShowRefund(false)}
                    className="flex-1 h-12 bg-gray-100 dark:bg-slate-700 text-gray-700 dark:text-gray-300 rounded-xl font-medium">Cancel</button>
                  <button onClick={handleRefund} disabled={refundLoading}
                    className="flex-[2] h-12 bg-red-600 text-white rounded-xl font-semibold flex items-center justify-center gap-2 disabled:opacity-50"
                    data-testid="confirm-refund">
                    {refundLoading ? <Loader2 className="h-5 w-5 animate-spin" /> : 'Request Refund'}
                  </button>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Loyalty Card */}
        {loyalty && (
          <div className="bg-white dark:bg-slate-800 rounded-3xl shadow-lg overflow-hidden mb-4">
            <div className={`p-5 ${loyalty.current_tier >= 4 ? 'bg-gradient-to-r from-amber-50 to-yellow-50 dark:from-amber-900/20 dark:to-yellow-900/20' : 'bg-gradient-to-r from-primary/5 to-primary/10'}`}>
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <span className="text-2xl">{loyalty.tier_emoji}</span>
                  <div>
                    <span className="font-bold text-secondary-900 dark:text-white">{loyalty.tier_name}</span>
                    {loyalty.current_tier > 0 && <span className="text-xs text-muted-foreground ml-1">Tier {loyalty.current_tier}</span>}
                  </div>
                </div>
                {loyalty.discount_percent > 0 && (
                  <div className="bg-green-500 text-white px-3 py-1 rounded-full text-sm font-bold">{loyalty.discount_percent}% OFF</div>
                )}
              </div>

              {/* Progress bar */}
              <div className="mb-2">
                <div className="flex justify-between text-xs text-muted-foreground mb-1">
                  <span>{loyalty.lifetime_orders} orders</span>
                  {loyalty.next_tier_name && <span>{loyalty.next_tier_emoji} {loyalty.next_tier_name}</span>}
                </div>
                <div className="w-full bg-gray-200 dark:bg-slate-600 rounded-full h-3">
                  <div className={`h-3 rounded-full transition-all duration-500 ${
                    loyalty.current_tier >= 7 ? 'bg-gradient-to-r from-amber-400 to-yellow-500' :
                    loyalty.current_tier >= 4 ? 'bg-gradient-to-r from-blue-400 to-purple-500' :
                    'bg-gradient-to-r from-primary to-amber-500'
                  }`} style={{ width: `${loyalty.progress_percent}%` }} />
                </div>
              </div>

              <p className="text-xs text-muted-foreground">
                {loyalty.orders_to_next > 0
                  ? `${loyalty.orders_to_next} more order${loyalty.orders_to_next !== 1 ? 's' : ''} to reach ${loyalty.next_tier_name} (${loyalty.next_tier_discount}% off${loyalty.next_tier_free_item ? ' + free ' + loyalty.next_tier_free_item.replace('_', ' ') : ''})`
                  : 'You have reached the highest tier!'}
              </p>

              {loyalty.free_item && (
                <div className="mt-2 bg-white/60 dark:bg-slate-700/60 rounded-lg px-3 py-2 text-xs">
                  <span className="font-medium text-green-700 dark:text-green-400">
                    Your perk: Free {loyalty.free_item.replace('_and_', ' & ').replace('_', ' ')} with every order
                  </span>
                </div>
              )}

              {loyalty.custom_offer && (
                <div className="mt-2 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg px-3 py-2 text-xs">
                  <span className="font-bold text-red-600">Special Offer: </span>
                  <span className="text-red-700 dark:text-red-300">{loyalty.custom_offer.description}</span>
                </div>
              )}
            </div>

            {/* Tier roadmap */}
            {loyalty.tiers && (
              <div className="px-5 py-3 border-t border-border/50 dark:border-slate-700">
                <p className="text-xs font-medium text-muted-foreground mb-2">Rewards Roadmap</p>
                <div className="flex gap-1 overflow-x-auto pb-1">
                  {loyalty.tiers.slice(0, 6).map(t => (
                    <div key={t.tier} className={`flex-shrink-0 px-2 py-1 rounded-lg text-[10px] text-center ${
                      t.tier <= loyalty.current_tier
                        ? 'bg-primary/10 text-primary font-bold'
                        : t.tier === loyalty.current_tier + 1
                          ? 'bg-amber-50 text-amber-700 border border-amber-200'
                          : 'bg-gray-50 dark:bg-slate-700 text-muted-foreground'
                    }`}>
                      <div>{t.emoji}</div>
                      <div>{t.discount_percent}%</div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Theme Toggle */}
        <motion.button
          whileTap={{ scale: 0.98 }}
          onClick={toggleTheme}
          className="w-full bg-white dark:bg-slate-800 rounded-3xl shadow-lg p-4 flex items-center gap-4 mb-4"
          data-testid="theme-toggle-btn"
        >
          <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
            theme === 'dark' ? 'bg-slate-700' : 'bg-amber-100'
          }`}>
            {theme === 'dark' ? (
              <Moon className="h-5 w-5 text-amber-300" />
            ) : (
              <Sun className="h-5 w-5 text-amber-500" />
            )}
          </div>
          <span className="flex-1 text-left font-medium text-secondary-900 dark:text-white">
            {theme === 'dark' ? 'Dark Mode' : 'Light Mode'}
          </span>
          <div className={`w-12 h-7 rounded-full p-1 transition-colors ${
            theme === 'dark' ? 'bg-primary' : 'bg-slate-200'
          }`}>
            <motion.div
              className="w-5 h-5 bg-white rounded-full shadow"
              animate={{ x: theme === 'dark' ? 20 : 0 }}
              transition={{ type: 'spring', stiffness: 500, damping: 30 }}
            />
          </div>
        </motion.button>

        {/* WhatsApp Notifications */}
        <div className="bg-white dark:bg-slate-800 rounded-3xl shadow-lg p-4 mb-6">
          <div className="flex items-center gap-4">
            <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
              whatsappEnabled ? 'bg-green-100 dark:bg-green-900/30' : 'bg-slate-100 dark:bg-slate-700'
            }`}>
              <MessageCircle className={`h-5 w-5 ${whatsappEnabled ? 'text-green-600' : 'text-slate-500'}`} />
            </div>
            <div className="flex-1">
              <p className="font-medium text-secondary-900 dark:text-white">WhatsApp Notifications</p>
              <p className="text-xs text-muted-foreground">
                {whatsappEnabled ? 'Receive order updates via WhatsApp' : 'Get notified about your orders'}
              </p>
            </div>
            <button
              onClick={handleWhatsappToggle}
              disabled={savingWhatsapp}
              className={`w-12 h-7 rounded-full p-1 transition-colors ${
                whatsappEnabled ? 'bg-green-500' : 'bg-slate-200 dark:bg-slate-600'
              }`}
              data-testid="whatsapp-toggle"
            >
              {savingWhatsapp ? (
                <div className="w-5 h-5 flex items-center justify-center">
                  <Loader2 className="h-4 w-4 animate-spin text-white" />
                </div>
              ) : (
                <motion.div
                  className="w-5 h-5 bg-white rounded-full shadow flex items-center justify-center"
                  animate={{ x: whatsappEnabled ? 20 : 0 }}
                  transition={{ type: 'spring', stiffness: 500, damping: 30 }}
                >
                  {whatsappEnabled && <Check className="h-3 w-3 text-green-500" />}
                </motion.div>
              )}
            </button>
          </div>
          
          {whatsappEnabled && whatsappNumber && (
            <div className="mt-3 pt-3 border-t border-border dark:border-slate-700">
              <p className="text-xs text-muted-foreground">
                Notifications sent to: <span className="font-medium text-secondary-900 dark:text-white">{whatsappNumber}</span>
              </p>
            </div>
          )}
        </div>

        {/* SMS Notifications */}
        {(user?.phone || whatsappNumber) && (
          <div className="bg-white dark:bg-slate-800 rounded-3xl shadow-lg p-4 mb-6" data-testid="sms-notifications-card">
            <div className="flex items-center gap-4">
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                smsEnabled ? 'bg-blue-100 dark:bg-blue-900/30' : 'bg-slate-100 dark:bg-slate-700'
              }`}>
                <Phone className={`h-5 w-5 ${smsEnabled ? 'text-blue-600' : 'text-slate-500'}`} />
              </div>
              <div className="flex-1">
                <p className="font-medium text-secondary-900 dark:text-white">SMS Notifications</p>
                <p className="text-xs text-muted-foreground">
                  Verification codes always arrive by SMS. Order updates too, when enabled by the canteen.
                </p>
              </div>
              <button
                onClick={handleSmsToggle}
                disabled={savingSms}
                className={`w-12 h-7 rounded-full p-1 transition-colors ${
                  smsEnabled ? 'bg-blue-500' : 'bg-slate-200 dark:bg-slate-600'
                }`}
                data-testid="sms-toggle"
              >
                {savingSms ? (
                  <div className="w-5 h-5 flex items-center justify-center">
                    <Loader2 className="h-4 w-4 animate-spin text-white" />
                  </div>
                ) : (
                  <motion.div
                    className="w-5 h-5 bg-white rounded-full shadow flex items-center justify-center"
                    animate={{ x: smsEnabled ? 20 : 0 }}
                    transition={{ type: 'spring', stiffness: 500, damping: 30 }}
                  >
                    {smsEnabled && <Check className="h-3 w-3 text-blue-500" />}
                  </motion.div>
                )}
              </button>
            </div>
          </div>
        )}

        {/* WhatsApp Setup Modal */}
        {showWhatsappSetup && (
          <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={() => setShowWhatsappSetup(false)}>
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              className="bg-white dark:bg-slate-800 rounded-2xl p-5 max-w-sm w-full"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center gap-3 mb-4">
                <div className="p-3 bg-green-100 dark:bg-green-900/30 rounded-xl">
                  <MessageCircle className="h-6 w-6 text-green-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-lg dark:text-white">Enable WhatsApp</h3>
                  <p className="text-sm text-muted-foreground">Get order notifications</p>
                </div>
              </div>
              
              <div className="mb-4">
                <label className="text-sm font-medium text-secondary-900 dark:text-white mb-2 block">
                  WhatsApp Number
                </label>
                <input
                  type="tel"
                  value={whatsappNumber}
                  onChange={(e) => setWhatsappNumber(e.target.value)}
                  placeholder="+27 82 123 4567"
                  className="w-full px-4 py-3 bg-slate-50 dark:bg-slate-700 rounded-xl border-0 text-secondary-900 dark:text-white"
                  data-testid="whatsapp-number-input"
                />
                <p className="text-xs text-muted-foreground mt-2">
                  We'll send you updates when your order is confirmed, paid, and ready for collection.
                </p>
              </div>

              <div className="flex gap-3">
                <button
                  onClick={() => setShowWhatsappSetup(false)}
                  className="flex-1 py-3 bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-300 rounded-xl font-medium"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveWhatsappNumber}
                  disabled={savingWhatsapp}
                  className="flex-1 py-3 bg-green-500 text-white rounded-xl font-medium flex items-center justify-center gap-2"
                >
                  {savingWhatsapp ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <>
                      <Bell className="h-4 w-4" />
                      Enable
                    </>
                  )}
                </button>
              </div>
            </motion.div>
          </div>
        )}

        {/* Menu Items */}
        <div className="bg-white dark:bg-slate-800 rounded-3xl shadow-lg overflow-hidden mb-6">
          {menuItems.map((item, idx) => {
            const Icon = item.icon;
            return (
              <motion.button
                key={item.label}
                whileTap={{ scale: 0.98 }}
                onClick={() => navigate(item.path)}
                className={`w-full flex items-center gap-4 p-4 border-b border-border dark:border-slate-700 last:border-0 ${
                  item.admin ? 'bg-primary/5 dark:bg-primary/10' : ''
                }`}
              >
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                  item.accent ? 'bg-red-100 dark:bg-red-900/30' : item.admin ? 'bg-primary' : 'bg-muted dark:bg-slate-700'
                }`}>
                  <Icon className={`h-5 w-5 ${
                    item.accent ? 'text-red-600 dark:text-red-400' : item.admin ? 'text-white' : 'text-secondary-900 dark:text-white'
                  }`} />
                </div>
                <span className={`flex-1 text-left font-medium ${
                  item.admin ? 'text-primary' : 'text-secondary-900 dark:text-white'
                }`}>
                  {item.label}
                </span>
                <ChevronRight className="h-5 w-5 text-muted-foreground" />
              </motion.button>
            );
          })}
        </div>

        {/* Logout */}
        <motion.button
          whileTap={{ scale: 0.98 }}
          onClick={handleLogout}
          className="w-full bg-red-50 dark:bg-red-900/30 text-red-600 dark:text-red-400 rounded-2xl p-4 flex items-center justify-center gap-2 font-medium mb-24"
          data-testid="logout-btn"
        >
          <LogOut className="h-5 w-5" />
          Sign Out
        </motion.button>
      </div>
    </motion.div>
  );
}
